/* eslint-disable sonarjs/cognitive-complexity */
import React from 'react';
import classNames from 'classnames';
import s from './ImageLinkContainer.scss';
import {withGlobals} from '../../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../../../gallery/galleryGlobalStrategy';
import {HoverType, IProduct, RibbonPlacementId} from '../../../../types/galleryTypes';
import {ProductMedia} from '../ProductMedia/ProductMedia';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';
import {withTranslations} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {canAddProductToCart} from '../productItemUtils';
import {ProductRibbon} from '../Ribbon/Ribbon';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {Experiments} from '../../../../constants';
import {LazyProductQuickAdd} from '../ProductQuickAdd/LazyProductQuickAdd';
import {AdditionalRibbons} from '../AdditionalRibbons/AdditionalRibbons';

export type ImageLinkContainerProps = IGalleryGlobalProps & {
  index: number;
  product: IProduct;
  showHoverPreview: boolean;
  renderAddToCartButton: () => React.JSX.Element;
  renderQuickViewButton: () => React.JSX.Element;
  handleProductItemClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  quantity: number;
  productItemRef: React.RefObject<HTMLDivElement>;
  isQuickAddOpen: boolean;
};

export enum DataHook {
  Root = 'product-item-container',
  QuickAddInsideImageContainer = 'quick-add-inside-image-container',
  RibbonsGroupOnImage = 'ribbons-group-on-image',
}

export const ImageLinkContainer: React.FunctionComponent<ImageLinkContainerProps> = withGlobals(
  withTranslations()((props: ImageLinkContainerProps) => {
    const {
      product,
      product: {id: productId, ribbon},
      index,
      showHoverPreview,
      renderAddToCartButton,
      renderQuickViewButton,
      handleProductItemClick,
      productItemRef,
      quantity,
      isQuickAddOpen,
      globals: {
        isLiveSiteMode,
        isPreviewMode,
        productsManifest,
        productsVariantInfoMap,
        shouldShowMobile,
        imageMode,
        imageRatio,
        textsMap: {digitalProductBadgeAriaLabelText},
        styles,
        stylesParams,
      },
    } = props;

    const {experiments} = useExperiments();

    const isAddToCartButtonOnImageAllowedInViewer = experiments.enabled(
      Experiments.AllowAddToCartButtonOnImageInViewer
    );
    const isShowWishlistInGalleryEnabled = experiments.enabled(Experiments.ShowWishlistInGallery);
    const isShowMultiRibbonsInGalleryEnabled = experiments.enabled(Experiments.ShowMultiRibbonsInGallery);
    const isAdditionalRibbonsFieldGraphQLEnabled = experiments.enabled(Experiments.AdditionalRibbonsFieldGraphQL);

    const productPageUrl = productsManifest[productId].url;

    const productVariantInfo = productsVariantInfoMap?.[productId];

    const currentMedia = productVariantInfo?.mediaItems ?? product.media;

    const hoverType = styles.get(stylesParams.gallery_hoverType).value;

    const renderImage = () => {
      const productWithCurrentMedia = canAddProductToCart(product, productsManifest)
        ? {
            ...product,
            media: currentMedia,
          }
        : product;

      const renderOnImageRibbon = () => {
        return (
          <ProductRibbon shouldShowMobile={shouldShowMobile} ribbon={ribbon} placement={RibbonPlacementId.OnImage} />
        );
      };

      const renderMultiRibbonsOnImage = (additionalRibbons: string[]) => (
        <ConditionalRender by={'shouldShowAnyRibbonOnImage'} className={s.ribbonContainerOnImage}>
          <div className={s.ribbonsGroupOnImage} data-hook={DataHook.RibbonsGroupOnImage}>
            {product.ribbon && (
              <ConditionalRender by={'shouldShowRibbonOnImage'} className={s.ribbonWrapper}>
                {renderOnImageRibbon()}
              </ConditionalRender>
            )}
            {additionalRibbons.length > 0 && (
              <AdditionalRibbons ribbons={additionalRibbons} placement={RibbonPlacementId.OnImage} />
            )}
          </div>
        </ConditionalRender>
      );

      const renderPrimaryRibbonOnImage = () => (
        <ConditionalRender by={'shouldShowRibbonOnImage'} className={s.ribbonContainerOnImage}>
          {renderOnImageRibbon()}
        </ConditionalRender>
      );

      const renderRibbonsGroupOnImage = () => {
        const additionalRibbonsData = isAdditionalRibbonsFieldGraphQLEnabled ? product.additionalRibbons ?? [] : [];
        const additionalRibbons: string[] = additionalRibbonsData.map((r) => r.name);
        return additionalRibbons.length > 0
          ? renderMultiRibbonsOnImage(additionalRibbons)
          : product.ribbon && renderPrimaryRibbonOnImage();
      };

      return (
        <ProductMedia
          classNames={{thumbnail: s.productThumbnail, image: s.productImage}}
          product={productWithCurrentMedia}
          isMobile={shouldShowMobile}
          hoverType={hoverType}
          imageRatioId={imageRatio}
          imageModeId={imageMode}
          globals={props.globals}
          textsMap={{digitalProductBadgeAriaLabel: digitalProductBadgeAriaLabelText}}
          ribbon={
            isShowMultiRibbonsInGalleryEnabled
              ? renderRibbonsGroupOnImage()
              : product.ribbon && (
                  <ConditionalRender
                    by={'shouldShowRibbonOnImage'}
                    className={classNames({
                      [s.oldRibbonContainerOnImage]: !isShowWishlistInGalleryEnabled,
                      [s.ribbonContainerOnImage]: isShowWishlistInGalleryEnabled,
                    })}>
                    {renderOnImageRibbon()}
                  </ConditionalRender>
                )
          }>
          {!shouldShowMobile && (
            <ConditionalRender by={'showQuickView'} className={s.quickViewButtonContainer}>
              {renderQuickViewButton()}
            </ConditionalRender>
          )}
          {isAddToCartButtonOnImageAllowedInViewer && (
            <ConditionalRender by="showAddToCartOnImage" className={s.addToCartButtonOnImageContainer}>
              {isQuickAddOpen ? (
                <div
                  className={classNames(s.productQuickAddContainer, {[s.isDesktop]: !shouldShowMobile})}
                  data-hook={DataHook.QuickAddInsideImageContainer}>
                  {
                    <LazyProductQuickAdd
                      product={product}
                      productIndex={index}
                      productItemRef={productItemRef}
                      quantity={quantity}
                    />
                  }
                </div>
              ) : (
                <div className={s.addToCartButtonContainer}>{renderAddToCartButton()}</div>
              )}
            </ConditionalRender>
          )}
        </ProductMedia>
      );
    };

    const classes = classNames(
      s.productItemLink,
      s.customizePadding,
      shouldShowMobile || (hoverType === HoverType.Alternate && currentMedia.length < 2) ? s.none : s[hoverType],
      {
        [s.hoverPreview]: showHoverPreview,
        [s.quickAddOpen]: isQuickAddOpen,
      }
    );

    return (
      <a
        href={isLiveSiteMode || isPreviewMode ? productPageUrl : null}
        tabIndex={-1}
        onClick={handleProductItemClick}
        className={classes}
        data-hook={DataHook.Root}>
        {renderImage()}
      </a>
    );
  })
);
